'use client'
import React from "react";
import Link from "next/link";
import {
  ArrowRight,
  Facebook,
  Gift,
  Twitter,
  Linkedin,
  Instagram,
  Mail,
  Phone,
  MapPin,
} from "lucide-react";
import { TFooterConfig } from "@/app/config";

const socialMediaData = [
  { icon: Facebook },
  { icon: Twitter },
  { icon: Linkedin },
  { icon: Instagram },
];

const contactData = [
  {
    icon: Mail,

  },
  {
    icon: Phone,

  },
  {
    icon: MapPin,

  },
];

export default function Footer({ config }: { config: TFooterConfig['footer'] }) {
  return (
    <footer className="w-full bg-[#1d2838] border-t border-gray-700">
      <div className="max-w-[1120px] mx-auto px-4 md:px-6 lg:px-8 py-12 md:py-16 lg:pt-[57px] lg:pb-14">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10 lg:gap-7 mb-8 md:mb-12 lg:mb-[42px]">
          {/* Company Info */}
          <div className="flex flex-col gap-4 md:gap-5 lg:gap-[21px]">
            {/* Logo */}
            <Link href={config.logoItems.href} className="flex items-center gap-2 lg:gap-[10.5px]">
              <div className="flex w-7 h-7 items-center justify-center bg-[#ffbf00] rounded-lg lg:rounded-[8.75px]">
                <img
                  className="w-7 h-7"
                  src={config.logoItems.logo}
                  alt={config.logoItems.label}
                />
              </div>
              <div className="text-white text-lg md:text-xl lg:text-[21px] leading-7 [font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal tracking-[0]">
                {config.logoItems.label}
              </div>
            </Link>

            {/* Description */}
            <div className="[font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal text-white text-sm md:text-base lg:text-sm tracking-[0] leading-relaxed lg:leading-[22.8px]">
              {config.description}
            </div>

            {/* Social Media */}
            <div className="flex items-center gap-2 lg:gap-3">
              {config.socials.map((social, index) => {
                const socialIcon = socialMediaData[index];
                return (
                  <div
                    key={index}
                    className="flex w-9 h-9 md:w-10 md:h-10 lg:w-[35px] lg:h-[35px] items-center justify-center bg-[#495565] rounded-lg lg:rounded-[8.75px] hover:bg-[#5a6b7a] transition-colors duration-200 cursor-pointer"
                  >
                    {socialIcon && <socialIcon.icon className="w-4 h-4 md:w-5 md:h-5 lg:w-4 lg:h-4 text-white" />}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Services */}
          <div className="flex flex-col gap-4 md:gap-5 lg:gap-[21px]">
            <div className="text-white text-sm md:text-base lg:text-sm leading-6 lg:leading-[21px] [font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal tracking-[0]">
              Our Services
            </div>
            <div className="flex flex-col gap-2 lg:gap-[10.5px]">
              {config.Our_Services.map((service) => (
                <Link
                  href={service.href}
                  key={service.id}
                  className="[font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal text-white text-sm md:text-base lg:text-sm tracking-[0] leading-6 lg:leading-[21px] hover:text-gray-300 transition-colors duration-200 cursor-pointer"
                >
                  {service.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Support */}
          <div className="flex flex-col gap-4 md:gap-5 lg:gap-[21px]">
            <div className="text-white text-sm md:text-base lg:text-sm leading-6 lg:leading-[21px] [font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal tracking-[0]">
              Support
            </div>
            <div className="flex flex-col gap-2 lg:gap-[10.5px]">
              {config.support.map((support, index) => (
                <Link
                  href={support.href}
                  key={index}
                  className="[font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal text-white text-sm md:text-base lg:text-sm tracking-[0] leading-6 lg:leading-[21px] hover:text-gray-300 transition-colors duration-200 cursor-pointer"
                >
                  {support.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Contact Info */}
          <div className="flex flex-col gap-4 md:gap-5 lg:gap-[21px]">
            <div className="text-white text-sm md:text-base lg:text-sm leading-6 lg:leading-[21px] [font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal tracking-[0]">
              Contact Info
            </div>
            <div className="flex flex-col gap-2 lg:gap-[10.5px]">
              {config.contactData.map((contact, index) => {
                const contactIcon = contactData[index];
                return (
                  <Link
                    href={contact.href}
                    key={index}
                    className="flex items-center gap-2 lg:gap-[10.5px]"
                  >
                    <contactIcon.icon className="w-4 h-4 md:w-5 md:h-5 lg:w-3.5 lg:h-3.5 text-white flex-shrink-0" />
                    <div className="[font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal text-white text-sm md:text-base lg:text-sm tracking-[0] leading-6 lg:leading-[21px] break-words">
                      {contact.text}
                    </div>
                  </Link>
                )
              })}
            </div>
          </div>
        </div>

        {/* App Download Callout */}
        <div className="mb-8 md:mb-10 lg:mb-12">
          <div className="relative overflow-hidden rounded-3xl border border-[#2f3e55] bg-[#121a26] p-5 md:p-7">
            <div className="pointer-events-none absolute -right-12 -top-12 h-36 w-36 rounded-full bg-[#ffbf00]/30 blur-3xl" />
            <div className="pointer-events-none absolute -left-12 bottom-0 h-32 w-32 rounded-full bg-[#0ea5e9]/20 blur-3xl" />

            <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <div className="mt-1 rounded-xl bg-[#ffbf00] p-2 text-[#1d2838]">
                  <Gift className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="text-white text-lg md:text-xl [font-family:'Segoe_UI_Symbol-Regular',Helvetica] leading-7">
                    Get Zuma AI on Android
                  </h3>
                  <p className="text-gray-300 text-sm md:text-base mt-1 [font-family:'Segoe_UI_Symbol-Regular',Helvetica]">
                    Fresh build, cleaner speed, smarter AI.
                  </p>
                </div>
              </div>

              <Link
                href="/app-download"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-4 py-2.5 text-[#172033] text-sm font-semibold hover:bg-[#f5f7fb] transition-colors"
              >
                Open Download Page
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="pt-6 md:pt-8 lg:pt-[29px] border-t border-gray-700">
          <div className="text-center">
            <div className="[font-family:'Segoe_UI_Symbol-Regular',Helvetica] font-normal text-white text-sm md:text-base lg:text-sm tracking-[0] leading-6 lg:leading-[21px]">
              {config.copyright}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
